export class CookieUtils {
    private static isHttps(): boolean {
        return (
            typeof window !== 'undefined' &&
            window.location?.protocol === 'https:'
        );
    }

    private static isLocalhost(): boolean {
        const h = window.location.hostname;

        return [
            'localhost',
            '127.0.0.1',
            'local.globalaccount.ford.com',
        ].includes(h);
    }

    private static normalizeDomain(domain?: string): string | undefined {
        if (!domain) return;
        const host = window.location.hostname;
        const cleaned = domain
            .replace(/^https?:\/\//, '')
            .split(':')[0]
            .trim();
        if (!cleaned) return;
        if (host === cleaned || host.endsWith(`.${cleaned}`)) return cleaned;
    }

    static setFMACookie(
        name: string,
        value: string,
        domain: string,
        minutes?: number
    ): void {
        if (typeof document === 'undefined') return;
        if (!name) return;

        const buildCookie = (useDomain?: string): string => {
            const parts: string[] = [];
            parts.push(
                `${encodeURIComponent(name)}=${encodeURIComponent(value ?? '')}`
            );

            if (typeof minutes === 'number' && Number.isFinite(minutes)) {
                const maxAge = Math.max(0, Math.floor(minutes * 60));
                const exp = new Date(Date.now() + maxAge * 1000);
                parts.push(`Max-Age=${maxAge}`);
                parts.push(`Expires=${exp.toUTCString()}`);
            }

            parts.push('Path=/');
            if (useDomain) {
                const startsWithDot = useDomain.startsWith('.');
                const finalCookieDomain = startsWithDot
                    ? useDomain
                    : `.${useDomain}`;

                parts.push(`Domain=${finalCookieDomain}`);
            }

            const https = this.isHttps();
            const sameSite = https ? 'None' : 'Lax';
            parts.push(`SameSite=${sameSite}`);

            if (https) parts.push('Secure');

            parts.push('Priority=High');

            return parts.join('; ');
        };

        const normalized = this.isLocalhost()
            ? undefined
            : this.normalizeDomain(domain);

        const primaryCookie = buildCookie(normalized);
        document.cookie = primaryCookie;

        const wrote = document.cookie
            .split('; ')
            .some((c) => c.startsWith(`${encodeURIComponent(name)}=`));
        if (wrote) {
            return;
        }

        if (normalized) {
            const fallbackCookie = buildCookie();
            document.cookie = fallbackCookie;
            const fallbackWrote = document.cookie
                .split('; ')
                .some((c) => c.startsWith(`${encodeURIComponent(name)}=`));
            if (!fallbackWrote) {
                console.warn(
                    '[CookieUtils] Cookie still not set after fallback.',
                    {
                        protocol:
                            typeof window !== 'undefined'
                                ? window.location.protocol
                                : 'n/a',
                        host:
                            typeof window !== 'undefined'
                                ? window.location.host
                                : 'n/a',
                        attempted: { primaryCookie, fallbackCookie },
                    }
                );
            }
        } else {
            console.warn(
                '[CookieUtils] Cookie not set. Check protocol/host/port and potential blockers (e.g. third-party iframes).',
                {
                    protocol: window.location.protocol,
                    host: window.location.host,
                    attempted: primaryCookie,
                }
            );
        }
    }

    static get(name: string): string | null {
        if (typeof document === 'undefined') return null;
        const target = `${encodeURIComponent(name)}=`;
        const parts = (document.cookie || '').split('; ');
        for (const part of parts) {
            if (part.startsWith(target)) {
                const raw = part.slice(target.length);
                try {
                    return decodeURIComponent(raw);
                } catch {
                    return raw;
                }
            }
        }
        return null;
    }

    static has(name: string): boolean {
        return this.get(name) !== null;
    }

    static getAll(): Record<string, string> {
        const out: Record<string, string> = {};
        if (typeof document === 'undefined' || !document.cookie) return out;
        for (const part of document.cookie.split('; ')) {
            const eq = part.indexOf('=');
            const key = eq >= 0 ? part.slice(0, eq) : part;
            const val = eq >= 0 ? part.slice(eq + 1) : '';
            const k = decodeURIComponent(key);
            try {
                out[k] = decodeURIComponent(val);
            } catch {
                out[k] = val;
            }
        }
        return out;
    }
}
